<template>
    <component
        v-bind="routerLinkScope"
        :class="[$style['mds-card'], classObject, sizeClass]"
        :is="linkComponentType"
        :href="href"
        :data-mds-version="version"
    >
        <div v-if="label" :class="$style['mds-card__label']">
            <div :class="$style['mds-card__label-title']">{{ label }}</div>
        </div>
        <div
            v-if="supplementalContentFirst && hasSupplementalSlotContent"
            :class="$style['mds-card__supplemental-content']"
        >
            <slot name="mds-card-supplemental-content"></slot>
        </div>
        <component :is="headingType" :class="$style['mds-card__title']">
            <slot>{{ title }}</slot>
        </component>
        <div v-if="caption" :class="$style['mds-card__caption']" v-html="caption"></div>
        <div
            v-if="!supplementalContentFirst && hasSupplementalSlotContent"
            :class="$style['mds-card__supplemental-content']"
        >
            <slot name="mds-card-supplemental-content"></slot>
        </div>
        <div v-if="hasActionSlotContent" :class="$style['mds-card__action']">
            <slot name="mds-card-action"></slot>
        </div>
        <div v-if="metadata" :class="$style['mds-card__metadata']" v-html="metadata"></div>
    </component>
</template>

<script>
    import packageInfo from '../package.json';
    import { routerLinkProps, useRouting, getLinkTag } from '@mds/routable-vue3';
    import { computed } from 'vue';

    const { name, version } = packageInfo;

    export default {
        name: 'MdsCard',
        data() {
            return {
                version: `${name}-${version}`,
            };
        },
        props: {
            caption: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the card caption.',
                    support: ['v-html'],
                },
            },
            fullBleed: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, sets supplemental content to full bleed.',
                },
            },
            headingLevel: {
                type: Number,
                default: 2,
                validator(value) {
                    return value >= 1 && value <= 6;
                },
                docs: {
                    validation: '—',
                    description: 'Sets the `<h#>` heading level of the card title.',
                },
            },
            href: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'When provided, makes the entire card a link and sets the `href` path.',
                },
            },
            label: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the card label.',
                },
            },
            metadata: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the card metadata.',
                    support: ['v-html'],
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium', 'large'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `small`, `medium`, `large`.',
                    description: 'Sets the size of the card.',
                },
            },
            supplementalContentFirst: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, moves supplemental content to the top of the card.',
                },
            },
            title: {
                type: String,
                required: true,
                docs: {
                    validation: '**Required**',
                    description: 'Sets the card title.',
                },
            },
            ...routerLinkProps,
        },
        setup(props) {
            const { isRouterActive, routerLinkScope } = useRouting(props);
            const linkComponentType = computed(() => {
                let linkType = 'div';
                if (isRouterActive.value) {
                    linkType = getLinkTag(props.linkTag);
                } else if (props.href) {
                    linkType = 'a';
                }

                return linkType;
            });

            return {
                isRouterActive,
                routerLinkScope,
                linkComponentType,
            };
        },
        computed: {
            sizeClass() {
                return this.$style[`mds-card--${this.size}`];
            },
            headingType() {
                return `h${this.headingLevel}`;
            },
            classObject() {
                const obj = {};
                obj[this.$style['mds-card--full-bleed']] = this.fullBleed;
                obj[this.$style['mds-card--full-bleed-top-label-overlap']] =
                    this.supplementalContentFirst && this.fullBleed;

                return obj;
            },
            hasActionSlotContent() {
                return this.$slots['mds-card-action'];
            },
            hasSupplementalSlotContent() {
                return !!this.$slots['mds-card-supplemental-content'];
            },
        },
    };
</script>

<style lang="scss" module>
    @use './card.scss';
</style>
